import { useState, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import groupService from 'src/services/api/groupService';
import { Button, Label } from 'semantic-ui-react';
import { changeDocTitle } from 'src/components/helpers';
import AGTable from 'src/components/table/aggrid';
import {
  captureAgGridViewState,
  restoreAgGridViewState,
  getActiveFilters,
} from 'src/components/table/helpers';
import { AdminTableSection, AdminTableContent } from 'src/assets/styles/components/AdminTable';
import NewLeaderAnswers from 'src/containers/groups/newLeaders/NewLeaderAnswers';
import {
  NewLeadersRowDef,
  NEW_LEADER_CATEGORY_FILTER_COL,
} from 'src/components/common/rows/NewLeadersRow';

const CATEGORY_FILTERS = {
  all: { label: 'Todos', filter: null },
  grupo: { label: 'Grupos', filter: 'Grupo' },
  seminario: { label: 'Seminarios', filter: 'Seminario' },
};

const COLUMN_NAME_MAP = {
  createdAt: 'Fecha',
  [NEW_LEADER_CATEGORY_FILTER_COL]: 'Categoría',
  type: 'Tipo',
  his_name: 'Nombre El',
  her_name: 'Nombre Ella',
  survey: 'Autorizado por el líder',
};

const AllNewLeaders = (props) => {
  const [newLeaders, set_newLeaders] = useState([]);
  const [loading, set_loading] = useState(true);
  const [showNewLeaderForm, set_showNewLeaderForm] = useState(null);
  const [categoryFilter, set_categoryFilter] = useState('all');
  const [displayedCount, set_displayedCount] = useState(0);
  const [savedFilterModel, set_savedFilterModel] = useState(null);
  const [activeFilters, set_activeFilters] = useState({});
  const gridRef = useRef(null);
  const viewStateRef = useRef(null);

  const syncActiveFilters = useCallback((api) => {
    if (!api) {
      set_activeFilters({});
      return;
    }
    set_activeFilters(getActiveFilters(api, COLUMN_NAME_MAP));
  }, []);

  const openNewLeaderForm = useCallback((id) => {
    viewStateRef.current = captureAgGridViewState(gridRef.current?.api);
    set_showNewLeaderForm(id);
  }, []);

  const applyCategoryFilter = useCallback(
    (filterKey) => {
      set_categoryFilter(filterKey);
      const api = gridRef.current?.api;
      if (!api) return;

      const { filter } = CATEGORY_FILTERS[filterKey] || CATEGORY_FILTERS.all;
      const currentModel = { ...(api.getFilterModel() || {}) };

      if (!filter) {
        delete currentModel[NEW_LEADER_CATEGORY_FILTER_COL];
      } else {
        currentModel[NEW_LEADER_CATEGORY_FILTER_COL] = {
          filterType: 'text',
          type: 'equals',
          filter,
        };
      }

      const nextModel = Object.keys(currentModel).length ? currentModel : null;
      api.setFilterModel(nextModel);
      api.onFilterChanged();
      set_savedFilterModel(nextModel);
      set_displayedCount(api.getDisplayedRowCount());
      syncActiveFilters(api);
    },
    [syncActiveFilters]
  );

  const onGridReady = (params) => {
    if (gridRef.current) {
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
    }

    if (savedFilterModel) {
      params.api.setFilterModel(savedFilterModel);
      params.api.onFilterChanged();
      set_displayedCount(params.api.getDisplayedRowCount());
    } else if (categoryFilter !== 'all') {
      applyCategoryFilter(categoryFilter);
    } else {
      set_displayedCount(params.api.getDisplayedRowCount());
    }

    syncActiveFilters(params.api);
    restoreAgGridViewState(params.api, viewStateRef.current);
  };

  const onFilterChanged = () => {
    if (gridRef.current?.api) {
      const model = gridRef.current.api.getFilterModel();
      set_savedFilterModel(Object.keys(model || {}).length ? model : null);
      set_displayedCount(gridRef.current.api.getDisplayedRowCount());
      syncActiveFilters(gridRef.current.api);
    }
  };

  useEffect(() => {
    changeDocTitle('Nuevos Aspirantes a Lideres');
    getInfo();
  }, []);

  useEffect(() => {
    if (!loading && gridRef.current?.api && categoryFilter !== 'all') {
      applyCategoryFilter(categoryFilter);
    }
  }, [newLeaders, loading, categoryFilter, applyCategoryFilter]);

  function getInfo() {
    set_loading(true);
    groupService
      .getAllNewLeaders()
      .then((response) => {
        if (response.data.success) {
          set_loading(false);
          props.history.push('?keyword=');
          set_newLeaders(response.data.newLeaders);
        } else {
          set_loading(null);
        }
      })
      .catch((error) => {
        if (error.response) Swal.fire(error.response.data.message);
        set_loading(false);
      });
  }

  if (showNewLeaderForm) {
    return (
      <NewLeaderAnswers
        id={showNewLeaderForm}
        set_showAnswers={set_showNewLeaderForm}
        answers={newLeaders.find((form) => form.id === showNewLeaderForm)}
        getInfo={getInfo}
      />
    );
  }

  return (
    <AdminTableSection>
      <AdminTableContent>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Button primary disabled={loading} onClick={getInfo}>
            Recargar
          </Button>
          <Button.Group>
            {Object.entries(CATEGORY_FILTERS).map(([key, { label }]) => (
              <Button
                key={key}
                active={categoryFilter === key}
                disabled={loading}
                onClick={() => applyCategoryFilter(key)}
              >
                {label}
              </Button>
            ))}
          </Button.Group>
          {!loading && (
            <span style={{ color: '#666', fontSize: '0.95rem' }}>
              Mostrando {displayedCount} de {newLeaders.length} solicitudes
            </span>
          )}
        </div>
        {Object.keys(activeFilters).length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <strong style={{ marginRight: '8px' }}>Filtros activos:</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {Object.entries(activeFilters).map(([column, value]) => (
                <Label key={column} color="blue" style={{ margin: '4px 0', fontWeight: '400' }}>
                  <strong>{column}:</strong> {value}
                </Label>
              ))}
            </div>
          </div>
        )}
      </AdminTableContent>
      <div>
        {!!loading && <div className="loading">Cargando...</div>}
        <AGTable
          rowData={newLeaders}
          rowHeight={50}
          tableRef={gridRef}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          columnDefs={NewLeadersRowDef({
            set_showNewLeaderForm: openNewLeaderForm,
            loading,
          })}
          suppressDragLeaveHidesColumns={false}
        />
      </div>
    </AdminTableSection>
  );
};

export default AllNewLeaders;
